// Password-strength evaluation for the change-password modal — a 1:1 port of the native apps'
// `evaluatePasswordStrength` (iOS `Features/Settings/PasswordStrength.swift`, Android
// `features/PasswordStrength.kt`). The three platforms must agree on what "Strong" means, since
// they gate Save on the same rule against the same Wix endpoint. Keep them in lockstep.

export type PasswordClassification = 'weak' | 'medium' | 'strong';

export interface PasswordStrength {
  classification: PasswordClassification;
  hasLetters: boolean;
  hasNumbers: boolean;
  hasSymbols: boolean;
  meetsMinLength: boolean;
  /** How many of the four rules are satisfied (0–4). */
  satisfiedRuleCount: number;
}

/** Minimum length rule — matches native's `passwordMinLength`. */
export const PASSWORD_MIN_LENGTH = 8;

/** Determinate fill fraction of the strength bar, keyed to classification. Shared 1:1 with native. */
export const BAR_FRACTION: Record<PasswordClassification, number> = {
  weak: 0.5,
  medium: 0.7,
  strong: 1,
};

const LETTER = /\p{L}/u;
const NUMBER = /\p{N}/u;
// A symbol is anything that isn't a letter, a number, or whitespace (native's rule).
const SYMBOL = /[^\p{L}\p{N}\s]/u;

export function evaluatePasswordStrength(password: string): PasswordStrength {
  const hasLetters = LETTER.test(password);
  const hasNumbers = NUMBER.test(password);
  const hasSymbols = SYMBOL.test(password);
  const meetsMinLength = [...password].length >= PASSWORD_MIN_LENGTH;

  const satisfiedRuleCount = [hasLetters, hasNumbers, hasSymbols, meetsMinLength].filter(Boolean).length;
  const classification: PasswordClassification =
    satisfiedRuleCount === 4 ? 'strong' : satisfiedRuleCount === 3 ? 'medium' : 'weak';

  return { classification, hasLetters, hasNumbers, hasSymbols, meetsMinLength, satisfiedRuleCount };
}
